import React, { useState, useEffect } from "react";
import {
    Stack,
    Text,
    Group,
    Button,
    Badge,
    ActionIcon,
    Modal,
    Tooltip,
    Collapse,
    Checkbox,
    TextInput,
    Divider,
    Pagination,
} from "@mantine/core";
import { FaTrash, FaUserShield, FaSearch, FaTimes } from "react-icons/fa";
import ModalButtons from "components/ui/Modal/ModalButtons";
import Skeleton from "components/ui/Preloader/Skeleton";
import { LicenseManager } from "types/module/License";
import { capitalizeName } from "utils/TextUtils";
import { getManagerEmail } from "helpers";

interface LicenseManagersSectionProps {
    licenseManagers?: LicenseManager[];
    admins?: LicenseManager[];
    isEditable?: boolean;
    onRemoveManager?: (managerId: string) => void;
    onRemoveAllManagers?: () => void;
    availableManagers?: LicenseManager[];
    onAddManagers?: (managers: LicenseManager[]) => void;
    isLoading?: boolean;
    isOperationDisabled?: boolean;
    isAssigningManagers?: boolean;
    loadingRemoveManagerId?: string | null;
    isRemovingAll?: boolean;
}

const LicenseManagersSection: React.FC<LicenseManagersSectionProps> = ({
    licenseManagers = [],
    admins = [],
    isEditable = false,
    onRemoveManager,
    availableManagers = [],
    onAddManagers,
    isLoading = false,
    isOperationDisabled = false,
    isAssigningManagers = false,
    loadingRemoveManagerId = null,
}) => {
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [managerToRemove, setManagerToRemove] = useState<LicenseManager | null>(null);
    const [showAddSection, setShowAddSection] = useState<boolean>(false);
    const [selectedManagers, setSelectedManagers] = useState<LicenseManager[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [managersPage, setManagersPage] = useState<number>(1);
    const [availableManagersPage, setAvailableManagersPage] = useState<number>(1);
    const [allSelected, setAllSelected] = useState<boolean>(false);

    const handleRemoveClick = (manager: LicenseManager) => {
        setManagerToRemove(manager);
        setShowConfirmModal(true);
    };

    const handleConfirmRemove = () => {
        const managerEmail = getManagerEmail(managerToRemove);
        if (managerToRemove && onRemoveManager && managerEmail) {
            onRemoveManager(managerEmail);
        }
        setShowConfirmModal(false);
        setManagerToRemove(null);
    };

    const handleCancelRemove = () => {
        setShowConfirmModal(false);
        setManagerToRemove(null);
    };

    const handleToggleAddSection = () => {
        setShowAddSection(!showAddSection);
        setSelectedManagers([]);
        setSearchQuery("");
        setAvailableManagersPage(1);
    };

    const handleManagerToggle = (manager: LicenseManager) => {
        const managerEmail = getManagerEmail(manager);
        setSelectedManagers(prev => {
            const isSelected = prev.some(m => getManagerEmail(m) === managerEmail);
            if (isSelected) {
                return prev.filter(m => getManagerEmail(m) !== managerEmail);
            } else {
                return [...prev, manager];
            }
        });
    };

    const handleSelectAll = () => {
        if (allSelected) {
            setSelectedManagers([]);
            setAllSelected(false);
        } else {
            setSelectedManagers([...filteredAvailableManagers]);
            setAllSelected(true);
        }
    };

    const handleAddSelectedManagers = () => {
        if (onAddManagers && selectedManagers.length > 0) {
            onAddManagers(selectedManagers);
        }
    };

    const handleCancelAdd = () => {
        setShowAddSection(false);
        setSelectedManagers([]);
        setSearchQuery("");
        setAvailableManagersPage(1);
    };

    // Close add section when assignment completes
    const [wasAssigning, setWasAssigning] = useState(false);
    useEffect(() => {
        if (isAssigningManagers) {
            setWasAssigning(true);
        } else if (wasAssigning) {
            setWasAssigning(false);
            setShowAddSection(false);
            setSelectedManagers([]);
            setSearchQuery("");
            setAvailableManagersPage(1);
        }
    }, [isAssigningManagers]);

    // Reset available managers page when search query changes
    useEffect(() => {
        setAvailableManagersPage(1);
    }, [searchQuery]);

    const assignedManagerEmails = [...licenseManagers, ...admins].map(m => getManagerEmail(m)).filter(Boolean);

    const filteredAvailableManagers = availableManagers.filter(manager => {
        const managerEmail = getManagerEmail(manager);
        const matchesSearch = manager.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            managerEmail?.toLowerCase().includes(searchQuery.toLowerCase());
        const hasValidEmail = managerEmail !== '';
        const notAlreadyAssigned = !assignedManagerEmails.includes(managerEmail);
        const isNotAdmin = !manager.isAdmin;
        return matchesSearch && notAlreadyAssigned && hasValidEmail && isNotAdmin;
    });

    // Update allSelected state when selectedManagers changes
    useEffect(() => {
        setAllSelected(
            selectedManagers.length === filteredAvailableManagers.length &&
            filteredAvailableManagers.length > 0
        );
    }, [selectedManagers, filteredAvailableManagers]);

    const AVAILABLE_MANAGERS_PER_PAGE = 5;
    const totalAvailablePages = Math.ceil(filteredAvailableManagers.length / AVAILABLE_MANAGERS_PER_PAGE);
    const paginatedAvailableManagers = filteredAvailableManagers.slice(
        (availableManagersPage - 1) * AVAILABLE_MANAGERS_PER_PAGE,
        availableManagersPage * AVAILABLE_MANAGERS_PER_PAGE
    );

    const regularManagers = licenseManagers.filter(manager => !manager.isAdmin);

    const MANAGERS_PER_PAGE = 5;
    const totalManagerPages = Math.ceil(regularManagers.length / MANAGERS_PER_PAGE);
    const paginatedManagers = regularManagers.slice(
        (managersPage - 1) * MANAGERS_PER_PAGE,
        managersPage * MANAGERS_PER_PAGE
    );

    useEffect(() => {
        if (managersPage > totalManagerPages && totalManagerPages > 0) {
            setManagersPage(1);
        }
    }, [managersPage, totalManagerPages]);

    useEffect(() => {
        if (availableManagersPage > totalAvailablePages && totalAvailablePages > 0) {
            setAvailableManagersPage(1);
        }
    }, [availableManagersPage, totalAvailablePages]);


    return (
        <>
            <Stack gap="sm">
                <Group justify="space-between" align="center">
                    <Text fw={500} size="sm">License Pool Management</Text>
                    <Group gap="xs">
                        {isEditable && onAddManagers && !showAddSection && !isLoading && (
                            <Button
                                size="xs"
                                onClick={handleToggleAddSection}
                                loading={isAssigningManagers}
                                disabled={isOperationDisabled}
                            >
                                Assign License Manager
                            </Button>
                        )}
                    </Group>
                </Group>

                {isLoading && <Skeleton />}

                {!isLoading && (<>
                    {isEditable && (
                        <Collapse in={showAddSection} transitionDuration={300}>
                            <Stack gap="sm" p="md" style={{
                                backgroundColor: 'var(--mantine-color-blue-0)',
                                borderRadius: '8px',
                                border: '1px solid var(--mantine-color-blue-2)'
                            }}>
                                <Group justify="space-between" align="center">
                                    <Text fw={500} size="sm">Add License Managers</Text>
                                    <ActionIcon
                                        variant="subtle"
                                        color="gray"
                                        onClick={handleCancelAdd}
                                        aria-label="Close add section"
                                    >
                                        <FaTimes size={14} />
                                    </ActionIcon>
                                </Group>

                                <TextInput
                                    placeholder="Search license managers..."
                                    value={searchQuery}
                                    onChange={(event) => setSearchQuery(event.currentTarget.value)}
                                    leftSection={<FaSearch size={14} />}
                                    size="sm"
                                />

                                <Divider />

                                <Group justify="space-between" align="center">
                                    <Text size="xs" c="dimmed" fw={500}>
                                        Available Managers ({filteredAvailableManagers.length})
                                    </Text>
                                    {filteredAvailableManagers.length > 0 && (
                                        <Button
                                            variant="light"
                                            size="xs"
                                            onClick={handleSelectAll}
                                        >
                                            {allSelected ? "Deselect All" : "Select All"}
                                        </Button>
                                    )}
                                </Group>

                                <Stack gap="xs" mah={200} style={{ overflowY: 'auto' }}>
                                    {paginatedAvailableManagers.length > 0 ? (
                                        paginatedAvailableManagers.map((manager) => (
                                            <Checkbox
                                                key={getManagerEmail(manager)}
                                                label={`${capitalizeName(manager.name)}${manager.profile ? ` (${manager.profile})` : ''}`}
                                                checked={selectedManagers.some(m => getManagerEmail(m) === getManagerEmail(manager))}
                                                onChange={() => handleManagerToggle(manager)}
                                                size="sm"
                                            />
                                        ))
                                    ) : (
                                        <Text size="sm" c="dimmed" ta="center">
                                            {searchQuery ? `No managers found matching "${searchQuery}"` : 'No available managers to add'}
                                        </Text>
                                    )}
                                </Stack>

                                {totalAvailablePages > 1 && (
                                    <Group justify="center" mt="xs">
                                        <Pagination
                                            size="xs"
                                            total={totalAvailablePages}
                                            value={availableManagersPage}
                                            onChange={setAvailableManagersPage}
                                            withEdges={false}
                                        />
                                    </Group>
                                )}

                                <Group justify="flex-end" gap="xs">
                                    <Button
                                        variant="default"
                                        size="xs"
                                        onClick={handleCancelAdd}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        color="blue"
                                        size="xs"
                                        onClick={handleAddSelectedManagers}
                                        disabled={selectedManagers.length === 0 || isOperationDisabled}
                                        loading={isAssigningManagers}
                                    >
                                        Assign Selected ({selectedManagers.length})
                                    </Button>
                                </Group>
                            </Stack>
                        </Collapse>
                    )}
                    {admins.length > 0 && regularManagers.length === 0 && (
                        <div>
                            <Group gap="xs" mb="xs">
                                <Text size="xs" c="#495057" fw={500}>Administrators</Text>
                            </Group>
                            <Group gap="xs">
                                {admins.map((admin) => (
                                    <Tooltip
                                        key={admin.email || admin.Id}
                                        label="Automatically assigned as notification contact. Will receive low threshold notifications only if no other notification contacts are specified."
                                        multiline
                                        w={250}
                                        withArrow
                                    >
                                        <Badge
                                            variant="light"
                                            color="gray"
                                            size="lg"
                                            leftSection={<FaUserShield size={10} />}
                                            style={{ cursor: 'help', textTransform: 'none', fontWeight: 'normal' }}
                                        >
                                            {capitalizeName(admin.name)}
                                        </Badge>
                                    </Tooltip>
                                ))}
                            </Group>
                        </div>
                    )}

                    {regularManagers.length > 0 && (
                        <div>
                            <Stack gap="xs">
                                <Group gap="xs" mb="xs">
                                    <Text size="xs" c="#495057" fw={500}>License Managers</Text>
                                </Group>
                                {paginatedManagers.map((manager) => (
                                    <Group key={getManagerEmail(manager)} justify="space-between" align="center">
                                        <Badge variant="light" color="gray" size="lg" style={{ textTransform: 'none', fontWeight: 'normal' }}>
                                            {capitalizeName(manager.name)}
                                        </Badge>
                                        {isEditable && onRemoveManager && (
                                            <ActionIcon
                                                color="orange"
                                                variant="subtle"
                                                size="sm"
                                                onClick={() => handleRemoveClick(manager)}
                                                aria-label={`Remove ${capitalizeName(manager.name)}`}
                                                loading={loadingRemoveManagerId === getManagerEmail(manager)}
                                                disabled={isOperationDisabled || loadingRemoveManagerId !== null && loadingRemoveManagerId !== getManagerEmail(manager)}
                                            >
                                                <FaTrash size={12} />
                                            </ActionIcon>
                                        )}
                                    </Group>
                                ))}
                            </Stack>
                            {totalManagerPages > 1 && (
                                <Group justify="center" mt="xs">
                                    <Pagination
                                        size="xs"
                                        total={totalManagerPages}
                                        value={managersPage}
                                        onChange={setManagersPage}
                                        withEdges={false}
                                    />
                                </Group>
                            )}
                        </div>
                    )}
                </>
                )}
            </Stack>

            <Modal
                opened={showConfirmModal}
                onClose={handleCancelRemove}
                title="Remove License Manager"
                centered
                size="sm"
            >
                <Stack gap="md">
                    <Text>
                        Are you sure you want to remove <strong>{managerToRemove ? capitalizeName(managerToRemove.name) : ''}</strong> as a license manager for this pool?
                    </Text>
                    <ModalButtons>
                        <Button variant="default" onClick={handleCancelRemove}>
                            Cancel
                        </Button>
                        <Button color="red" onClick={handleConfirmRemove}>
                            Remove
                        </Button>
                    </ModalButtons>
                </Stack>
            </Modal>
        </>
    );
};

export default LicenseManagersSection;
